import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useT, useLanguage } from "@/i18n/LanguageContext";

// Generic legal-document viewer. Renders the bilingual HTML at
// /legal/<slug>-<lang>.html inside an iframe so the document keeps its own
// styling and is trivial to swap once legal counsel approves the final wording.
const DOCS = {
  privacy: { slug: "privacy", title: { en: "Privacy Policy", sr: "Politika privatnosti" } },
  cookies: { slug: "cookies", title: { en: "Cookie Policy", sr: "Politika kolačića" } },
  "purchase-terms": { slug: "purchase-terms", title: { en: "Terms of Purchase", sr: "Uslovi kupovine" } },
};
const BACK = { en: "Back to site", sr: "Nazad na sajt" };
const LOADING = { en: "Loading the document…", sr: "Učitavanje dokumenta…" };

export default function LegalDoc({ docKey }) {
  const t = useT();
  const { lang } = useLanguage();
  const [loaded, setLoaded] = useState(false);
  const doc = DOCS[docKey];
  const src = `/legal/${doc.slug}-${lang === "sr" ? "sr" : "en"}.html`;

  return (
    <main className="min-h-screen bg-[#FAFAF7] text-[#0A0A0F] flex flex-col">
      <header className="border-b border-[#E8E7E2] bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="legal-back"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#62646B] hover:text-[#0A0A0F]"
          >
            <ArrowLeft size={14} /> {t(BACK)}
          </Link>
          <h1 className="font-heading font-semibold tracking-tight text-base sm:text-lg">
            {t(doc.title)}
          </h1>
          <div className="flex items-center gap-3">
            <img src="/img/winn-coin.png" alt="Winfield" className="w-8 h-8 object-contain" />
            <span className="font-heading font-semibold tracking-tight hidden sm:inline">$WINN</span>
          </div>
        </div>
      </header>

      <div className="flex-1 relative">
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-[#62646B] z-10 bg-[#FAFAF7]">
            <span className="inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> {t(LOADING)}
            </span>
          </div>
        )}
        <iframe
          key={src}
          src={src}
          title={t(doc.title)}
          data-testid="legal-iframe"
          onLoad={() => setLoaded(true)}
          className="w-full h-[calc(100vh-4rem)] border-0 bg-white"
        />
      </div>
    </main>
  );
}
